import { ReactNode } from "react"

import { CalendarIcon, CubeIcon, CurrencyDollarIcon, ShareIcon, UserIcon } from "@heroicons/react/24/outline"
import { Link } from "react-router-dom"
import { twMerge } from "tailwind-merge"

import { truncateEmail } from "~/src/lib/email"
import { Format } from "~/src/lib/format"

const iconMap = {
  CalendarIcon: CalendarIcon,
  UserIcon: UserIcon,
  CubeIcon: CubeIcon,
  CurrencyDollarIcon: CurrencyDollarIcon,
  ShareIcon: ShareIcon,
}

export type LookbookDetailPanelsData = {
  dateCreated: string
  projectOwner: string
  quantity?: string
  budget?: string
  sharedWith?: { sampleColleagueEmail: string; totalCount: number }
  showDetails?: boolean
  className?: string
}

export function hasPanelData({
  dateCreated: _dateCreated,
  showDetails: _showDetails,
  ...rest
}: LookbookDetailPanelsData): boolean {
  return Object.values(rest).some((value) => value)
}

function DetailPanel({
  iconName,
  title,
  details,
  className,
}: {
  iconName: string
  title: string
  details: string | number | ReactNode
  className?: string
}) {
  const IconComponent = iconMap[iconName]
  return (
    <div className={twMerge("w-full min-w-fit rounded-lg bg-white p-4 sm:w-48", className)}>
      <IconComponent className="mb-3 h-6 w-6 text-sky-300" />
      <p className="font-medium text-gray-900">{title}</p>
      <div className="text-wrap text-gray-500">{details}</div>
    </div>
  )
}

export function LookbookDetailPanels(props: LookbookDetailPanelsData) {
  const { dateCreated, projectOwner, quantity, budget, sharedWith, showDetails } = props

  // Subtract 1 from the total count to account for the sample colleague email
  const sharedWithOthersCount = sharedWith ? sharedWith.totalCount - 1 : 0

  return (
    <div className={twMerge(!showDetails && "hidden")}>
      <div className="mx-6 mt-8 flex flex-wrap gap-6 pb-8">
        <DetailPanel iconName="CalendarIcon" title="Date" details={dateCreated} />
        <DetailPanel iconName="UserIcon" title="Project Owner" details={projectOwner} />
        {quantity && <DetailPanel iconName="CubeIcon" title="Quantity" details={quantity} />}
        {budget && <DetailPanel iconName="CurrencyDollarIcon" title="Budget" details={budget} />}
        {sharedWith && (
          <DetailPanel
            iconName="ShareIcon"
            title="Shared with"
            className="col-span-2"
            details={
              <>
                <p>{truncateEmail(sharedWith.sampleColleagueEmail)}</p>
                {sharedWithOthersCount > 0 && (
                  <Link to="share" className="col-p-2 text-blue-600">
                    {`+ ${sharedWithOthersCount} ${Format.pluralize(sharedWithOthersCount, "other")}`}{" "}
                  </Link>
                )}
              </>
            }
          />
        )}
      </div>
    </div>
  )
}
